import { useState } from 'react';
import { usePage } from '@inertiajs/react';
import { UAParser } from 'ua-parser-js';
import { getCSRFToken } from '../utils/csrf';
import { dispatchCartCount } from '../utils/cartBadge';
import PromptBanner from './PromptBanner';

interface CartConflictProps {
    saved_cart: Array<{ id: number; format_id: number; quantity: number }>;
    meta?: {
        updated_at?: string;
        user_agent?: string;
    };
    type?: 'conflict' | 'restore';
}

function formatRelativeTime(isoDate: string): string {
    const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (diff < 1) return 'hace un momento';
    if (diff < 60) return `hace ${diff} minuto${diff > 1 ? 's' : ''}`;
    if (diff < 1440) {
        const h = Math.floor(diff / 60);
        return `hace ${h} hora${h > 1 ? 's' : ''}`;
    }
    const d = Math.floor(diff / 1440);
    return `hace ${d} día${d > 1 ? 's' : ''}`;
}

function parseDevice(ua: string): string {
    const parser = new UAParser(ua);
    const browser = parser.getBrowser().name || '';
    const os = parser.getOS().name || '';
    return [browser, os].filter(Boolean).join(' en ');
}

const CartIcon = (
    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
        <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </div>
);

const RESOLVED_KEY = 'cart_conflict_resolved';

function conflictKey(c: CartConflictProps): string {
    return c.meta?.updated_at || 'unknown';
}

export default function CartConflictModal() {
    const { cartConflict } = usePage<{ cartConflict?: CartConflictProps }>().props;
    const [open, setOpen] = useState(() => {
        if (!cartConflict) return false;
        try {
            return sessionStorage.getItem(RESOLVED_KEY) !== conflictKey(cartConflict);
        } catch {
            return true;
        }
    });
    const [loading, setLoading] = useState(false);

    if (!open || !cartConflict) return null;

    const count = cartConflict.saved_cart.filter((i) => i.quantity > 0).length;
    const meta = cartConflict.meta;
    const isRestore = cartConflict.type === 'restore';

    let detail = `${count} producto${count > 1 ? 's' : ''}`;
    if (meta?.updated_at) detail += ` que estabas armando ${formatRelativeTime(meta.updated_at)}`;
    if (meta?.user_agent) {
        const device = parseDevice(meta.user_agent);
        if (device) detail += ` desde ${device}`;
    }

    const msg = isRestore
        ? `Tenés un carrito guardado con ${detail}. ¿Querés restaurarlo?`
        : `Tenés un carrito con ${detail}. ¿Querés continuar con ese carrito?`;

    const dismiss = () => {
        console.log('[CartConflictModal] dismiss() called');
        setOpen(false);
        try { sessionStorage.setItem(RESOLVED_KEY, conflictKey(cartConflict)); } catch {}
        window.dispatchEvent(new CustomEvent('cart-conflict-resolved'));
    };

    const resolve = async (choice: 'saved' | 'current') => {
        console.log('[CartConflictModal] resolve() called with choice:', choice);
        setLoading(true);
        try {
            const res = await fetch('/api/cart/resolve-conflict', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': getCSRFToken(),
                },
                body: JSON.stringify({ choice }),
            });
            const data = await res.json();
            console.log('[CartConflictModal] API response:', data);
            dispatchCartCount(data.summary?.count ?? 0);
            dismiss();
        } catch (e) {
            console.error('[CartConflictModal] API error:', e);
            dismiss();
        }
    };

    return (
        <PromptBanner
            icon={CartIcon}
            title="Carrito guardado encontrado"
            description={msg}
            onClose={() => resolve('current')}
            dismissLabel={isRestore ? 'No, empezar vacío' : 'Continuar con el actual'}
            actionLabel={isRestore ? 'Sí, restaurar' : 'Usar el guardado'}
            onAction={() => resolve('saved')}
            loading={loading}
        />
    );
}
